import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Table } from "../../../components/SystemComponent/table";
import { GroupButton } from "../../../components/SystemComponent/GroupButton";
import { Badge } from "../../../components/SystemComponent/Bagde";
import { Modal } from "../../../components/SystemComponent/Modal";
import { Button } from "../../../components/SystemComponent/Button";
import { MdAdd, MdEdit, MdDelete, MdAccountBalance } from "react-icons/md";
import { createApi } from "../../../../core/api";

export const CompanyPackageItemBalanceCrud = () => {
  const api = useMemo(() => createApi(), []);
  const { setPageMenus } = useOutletContext<any>();

  const [data, setData] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);

  const [companyId, setCompanyId] = useState<number | "">("");
  const [packageItemId, setPackageItemId] = useState<number | "">("");
  const [balance, setBalance] = useState<number | "">("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, c, i] = await Promise.all([
        api.companyPackageItemBalance.getAll(),
        api.company.getAll(),
        api.packageItem.getAll(),
      ]);

      if (b?.ok) setData(b.data?.data ?? b.data ?? []);
      if (c?.ok) setCompanies(c.data?.data ?? c.data ?? []);
      if (i?.ok) setItems(i.data?.data ?? i.data ?? []);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    setPageMenus([
      {
        label: "Company Package Item Balance",
        items: [
          {
            label: "Company Package Item Balance",
            route: "/dashboard/company-package-item-balance",
          },
        ],
      },
    ]);
    load();
    return () => setPageMenus([]);
  }, [load, setPageMenus]);

  const openCreate = () => {
    setEditing(null);
    setCompanyId("");
    setPackageItemId("");
    setBalance("");
    setError(null);
    setOpen(true);
  };

  const openEdit = (row: any) => {
    setEditing(row);
    setCompanyId(row.companyId);
    setPackageItemId(row.packageItemId);
    setBalance(row.balance);
    setError(null);
    setOpen(true);
  };

  const submit = async () => {
    if (!companyId || !packageItemId || balance === "" || saving) return;

    setSaving(true);
    setError(null);

    try {
      if (editing) {
        const r = await api.companyPackageItemBalance.update(editing.id, {
          companyId,
          packageItemId,
          balance,
        });
        if (!r?.ok) throw new Error(r?.message || "Update failed");
      } else {
        const r = await api.companyPackageItemBalance.create({
          companyId,
          packageItemId,
          balance,
        });
        if (!r?.ok) throw new Error(r?.message || "Create failed");
      }

      setOpen(false);
      load();
    } catch (e: any) {
      setError(e?.message || "Unexpected error occurred");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (id: number) => {
    setDeleteId(id);
    setDeleteError(null);
  };

  const doDelete = async () => {
    if (!deleteId || deleting) return;

    setDeleting(true);
    setDeleteError(null);

    try {
      const r = await api.companyPackageItemBalance.delete(deleteId);
      if (!r?.ok) throw new Error(r?.message || "Delete failed");

      setDeleteId(null);
      load();
    } catch (e: any) {
      setDeleteError(e?.message || "Unexpected error occurred");
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo<any[]>(
    () => [
      {
        key: "id",
        title: "ID",
        minWidth: 80,
        accessor: (r: any) => r.id,
      },
      {
        key: "company",
        title: "Company",
        minWidth: 260,
        accessor: (r: any) =>
          companies.find((x) => x.id === r.companyId)?.name ?? "",
        render: (_: any, v: string) => (
          <Badge label={v} color="#0EA5E9" bg="rgba(14,165,233,.15)" />
        ),
      },
      {
        key: "item",
        title: "Package Item",
        minWidth: 260,
        accessor: (r: any) =>
          items.find((x) => x.id === r.packageItemId)?.name ?? "",
        render: (_: any, v: string) => (
          <Badge
            icon={MdAccountBalance}
            label={v}
            color="#F59E0B"
            bg="rgba(245,158,11,.15)"
          />
        ),
      },
      {
        key: "balance",
        title: "Balance",
        minWidth: 160,
        accessor: (r: any) => r.balance,
      },
      {
        key: "actions",
        title: "Actions",
        minWidth: 200,
        render: (row: any) => (
          <GroupButton
            items={[
              {
                id: "edit",
                icon: MdEdit,
                color: "#2563EB",
                onClick: () => openEdit(row),
              },
              {
                id: "delete",
                icon: MdDelete,
                color: "#DC2626",
                onClick: () => confirmDelete(row.id),
              },
            ]}
          />
        ),
      },
    ],
    [companies, items]
  );

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div>
          <div style={S.title}>Company Package Item Balance</div>
          <div style={S.subTitle}>Manage company item balances</div>
        </div>

        <Button variant="primary" icon={MdAdd} onClick={openCreate}>
          Add Balance
        </Button>
      </div>

      <Table
        tableKey="company-package-item-balance"
        loading={loading}
        columns={columns}
        data={data}
        features={{
          sortable: true,
          filterable: true,
          pagination: true,
          persistence: true,
        }}
      />

      <Modal open={open} onClose={() => (!saving ? setOpen(false) : null)}>
        <div style={S.modalTitle}>
          {editing ? "Update Balance" : "Add Balance"}
        </div>

        <div style={S.form}>
          <label style={S.label}>Company</label>
          <select
            value={companyId}
            onChange={(e) => setCompanyId(Number(e.target.value))}
            style={S.input}
            disabled={saving}
          >
            <option value="">Select company</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          <label style={S.label}>Package Item</label>
          <select
            value={packageItemId}
            onChange={(e) => setPackageItemId(Number(e.target.value))}
            style={S.input}
            disabled={saving}
          >
            <option value="">Select item</option>
            {items.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name}
              </option>
            ))}
          </select>

          <label style={S.label}>Balance</label>
          <input
            type="number"
            value={balance}
            onChange={(e) => setBalance(Number(e.target.value))}
            style={S.input}
            disabled={saving}
          />

          {error && <div style={S.error}>{error}</div>}
        </div>

        <div style={S.actions}>
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={saving}
            disabled={saving}
          >
            Save
          </Button>
        </div>
      </Modal>

      <Modal
        open={!!deleteId}
        onClose={() => (!deleting ? setDeleteId(null) : null)}
      >
        <div style={S.modalTitle}>Delete Balance</div>

        <div style={{ fontSize: 14, color: "#334155" }}>
          Are you sure you want to delete this balance?
        </div>

        {deleteError && <div style={S.error}>{deleteError}</div>}

        <div style={S.actions}>
          <Button
            variant="ghost"
            onClick={() => setDeleteId(null)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={doDelete}
            loading={deleting}
            disabled={deleting}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  wrap: {
    position: "absolute",
    inset: 0,
    padding: 20,
    background: "#F8FAFC",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  title: {
    fontSize: 22,
    fontWeight: 800,
    color: "#0F172A",
  },
  subTitle: {
    fontSize: 13,
    color: "#64748B",
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 800,
    marginBottom: 14,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  label: {
    fontSize: 12,
    fontWeight: 700,
    color: "#334155",
  },
  input: {
    height: 42,
    borderRadius: 10,
    border: "1px solid #CBD5E1",
    padding: "0 12px",
    fontSize: 14,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 20,
  },
  error: {
    marginTop: 8,
    fontSize: 12,
    fontWeight: 600,
    color: "#DC2626",
  },
};
